<template>
  <div class="login-form">
    <div
      class="title free-tip-topLeft"
      :data-tip="$t('zhu-ce-li-xiang-7-tian-mian-fei-shi-yong')"
    >
      {{ $t('huan-ying-deng-lu-you-yan-fu-wu-ping-tai') }}
    </div>
    <div class="mt-[15px] text-[14px] h-[22px] text-font-80">
      {{ $t('qing-shi-yong-wei-xin-sao-miao-er-wei-ma') }}
    </div>
    <div class="qrcode-wrap">
      <div class="qrcode-container">
        <ClientOnly>
          <XQrcode
            :class="{
              qrcode: true,
              blur: !isProtocolConfirm || !qrcodeTicketInfo?.url,
            }"
            :url="qrcodeTicketInfo?.url || 'https://www.youyan3d.com'"
            :size="180"
          ></XQrcode>
        </ClientOnly>
        <div
          v-if="
            isFetchingQrcode ||
            isQrcodeTimeOuted ||
            isScanned ||
            !isProtocolConfirm
          "
          class="mask"
        >
          <div v-if="!isProtocolConfirm" class="qrcode-tip">请先确认协议</div>
          <a-spin v-if="isFetchingQrcode" :indicator="indicator" />
          <div v-if="isScanned" class="qrcode-tip">
            <div>二维码已使用</div>
            <div>
              <a href="javascript:;" @click="refreshQrcode">点击刷新</a>
            </div>
          </div>
          <div v-if="isQrcodeTimeOuted" class="qrcode-tip timeout">
            <div>二维码已过期</div>
            <div>
              <a href="javascript:;" @click="refreshQrcode">点击刷新</a>
            </div>
          </div>
        </div>
      </div>
      <div v-if="qrcodeTicketInfo" class="refresh-panel">
        {{ $t('xian-shi-yi-chang') }}
        <a class="refresh-button cursor-pointer" @click="refreshQrcode">
          {{ $t('dian-ji-shua-xin') }}
        </a>
      </div>
    </div>
    <a-checkbox
      v-model:checked="isProtocolConfirm"
      class="mt-[35px]"
      @change="(e) => handleProtocolChange(e.target.checked)"
    >
      {{ $t('wo-yi-yue-du-bing-tong-yi') }}
      <ALink
        :href="'https://frontend-protocol.xmov.ai/youyan-v3/user'"
        name="user"
      >
        {{ $t('mo-fa-you-yan-yong-hu-xie-yi') }}
      </ALink>
      {{ $t('he') }}
      <ALink
        :href="'https://frontend-protocol.xmov.ai/youyan-v3/privacy'"
        name="policy"
      >
        {{ $t('mo-fa-you-yan-yin-si-zheng-ce') }}
      </ALink>
    </a-checkbox>
    <div class="tip text-center">
      {{ $t('sao-ma-guan-zhu') }}
      <span class="colored">{{ $t('mo-fa-you-yan') }}</span>
      {{ $t('gong-zhong-hao') }} <br />
      {{ $t('wan-cheng-zhang-hao-bang-ding') }}
    </div>
    <ProtocolConfirm
      v-model:open="confirmProtocolOpen"
      @confirm="handleConfirmClick"
    />
    <BindPhone
      v-if="qrcodeTicketInfo?.ticket_id"
      ref="phoneRef"
      :ticketId="qrcodeTicketInfo?.ticket_id"
      @ok="onBindPhoneSuccess"
    />
  </div>
</template>

<script lang="ts" setup>
import { LoadingOutlined } from '@ant-design/icons-vue'
import { h } from 'vue'
import ProtocolConfirm from './protocolConfirm.vue'
import { trackData } from './trackData'
import BindPhone from '~/features/account/bindPhone/index.vue'
import {
  getWechatOfficialAccountScanStatus,
  getWechatOfficialAccountTicket,
  IScanStatus,
  type IWechatOfficialAccountTicket,
} from '~/api/module/open-login'

import XQrcode from '~/components/xQrcode/index.vue'
import { BROADCAST_KEY, COOKIE_KEY, SESSION_SOURCE } from '~/constants/config'
import { getUserInfo } from '~/api'
import type { LoginInfo } from '~/api/interface'

const indicator = h(LoadingOutlined, {
  style: {
    fontSize: '24px',
  },
  spin: true,
})

const isQrcodeTimeOuted = ref(false)
const isFetchingQrcode = ref(false)
const isProtocolConfirm = ref()
const confirmProtocolOpen = ref(false)
const qrcodeTicketInfo = ref<IWechatOfficialAccountTicket>()

// 倒计时
const countdown = ref()
let countdownTimer: number
function startQrcodeTimeOut(time: number) {
  countdown.value = time
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
  countdownTimer = window.setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      isQrcodeTimeOuted.value = true
    }
  }, 1000)
}

const phoneRef = ref<typeof BindPhone>()

// 状态轮询
let statusLoopTimer: number
const emits = defineEmits(['success', 'accountSelect'])
const { setAccountList, setSession, setUserInfo } = useChangeUser()
const isScanned = ref<boolean>(false)
async function startLoginStatusLoop(ticketId: string) {
  const res = await getWechatOfficialAccountScanStatus({ ticket_id: ticketId })

  if (res.data) {
    if (res.data.status === IScanStatus.PENDING) {
      if (statusLoopTimer) {
        window.clearTimeout(statusLoopTimer)
      }
      statusLoopTimer = window.setTimeout(() => {
        startLoginStatusLoop(ticketId)
      }, 5000)
    }
    if (res.data.action === 'register') {
      return phoneRef.value?.toggle()
    }
    if (res.data.status === IScanStatus.SUCCESS) {
      onBindPhoneSuccess(res.data.login_data)
    }
  }
}

async function refreshQrcode() {
  try {
    if (!isProtocolConfirm.value) {
      return (confirmProtocolOpen.value = true)
    }
    isFetchingQrcode.value = true
    isQrcodeTimeOuted.value = false
    isScanned.value = false
    const res = await getWechatOfficialAccountTicket()

    if (res.data) {
      qrcodeTicketInfo.value = res.data
      startQrcodeTimeOut(res.data.expired_in)
      startLoginStatusLoop(res.data.ticket_id)
    }
  } catch (err) {}
  isFetchingQrcode.value = false
}

function handleProtocolChange(val: boolean) {
  if (val && !qrcodeTicketInfo.value) {
    refreshQrcode()
  }
}

async function onBindPhoneSuccess(login_data: LoginInfo[]) {
  isScanned.value = true
  // 用户账号列表
  setAccountList(login_data)
  trackData(
    'wechat',
    { account_name: login_data[0].account_name },
    {
      NewUser: login_data[0]?.is_new_user ? USER_TYPE.NEW : USER_TYPE.OLD,
    }
  )
  if (login_data.length > 1) {
    emits('accountSelect', login_data)
  } else {
    // 用户token
    await setSession(login_data[0])
    // emitBroadcast(COOKIE_KEY.USER_TOKEN, {
    //   cookieValue: login_data[0],
    // })
    emitBroadcast(BROADCAST_KEY.REFRESH, { tabId: TAB_ID })
    sensorsLogin(login_data[0]?.user_id)
    const result = await getUserInfo()
    // 用户信息
    await setUserInfo(result.data!)
    emits('success', result.data)
  }
  sessionRemoveItem(SESSION_SOURCE)
}

function handleConfirmClick() {
  isProtocolConfirm.value = true
  confirmProtocolOpen.value = false
  refreshQrcode()
}

onBeforeUnmount(() => {
  window.clearInterval(statusLoopTimer)
  window.clearInterval(countdownTimer)
})
</script>

<style lang="scss" scoped>
.login-form {
  .title {
    @apply font-medium text-[36px];
  }
  .qrcode {
    width: 180px;
    &.blur {
      filter: blur(3px);
    }
  }
  .qrcode-wrap {
    @apply flex items-center justify-center;
    flex-direction: column;

    .qrcode-container {
      @apply flex items-center justify-center;
      overflow: hidden;
      position: relative;
      height: 180px;
      width: 180px;
      margin: 40px auto 16px;
      background-color: var(--back-6);

      .mask {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.3);
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .qrcode-tip {
      @apply items-center justify-center flex flex-col text-center;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      color: var(--text-1);
      width: 108px;
      gap: 4px;
      border-radius: 6px;
      padding: 10px 0;
      font-size: 14px;
      line-height: 20px;
      background: #ffffff;
      box-shadow: 0px -2px 12px 0px rgba(81, 80, 96, 0.2);
    }
  }
  .tip {
    margin-top: 48px;
    line-height: 1.4;
    .colored {
      color: var(--purple-5);
    }
  }
}
</style>
